import React from "react";
import { Box, Grid, Card, CardContent, Typography, Chip, Button, IconButton, Alert, Skeleton } from "@mui/material";
import { Visibility as ViewIcon, Refresh as RefreshIcon, KeyboardBackspace as BackIcon } from "@mui/icons-material";
import {
    useGetDocumentationListQuery,
    useGetDocumentationComponentQuery,
    useDownloadDocumentationMutation,
    useViewDocumentationMutation,
} from "../../../store/documentation/documentationApis";
import { parseDocumentationList, parseDocumentationComponent } from "../../../store/documentation/documentationParser";
import DocumentationCard from "./DocumentationCard";

const DocumentationManager = (props) => {
    const { selectedComponent, setSelectedComponent, setSelectedFile, clearSelection } = props;

    // API queries
    const {
        data: documentationListData,
        isLoading: isLoadingList,
        error: listError,
        refetch: refetchList,
    } = useGetDocumentationListQuery();

    const { data: componentData, isLoading: isLoadingComponent } = useGetDocumentationComponentQuery(
        selectedComponent,
        {
            skip: !selectedComponent,
        },
    );

    const [downloadDocumentation] = useDownloadDocumentationMutation();
    const [viewDocumentation] = useViewDocumentationMutation();

    // Parse data
    const documentationList = parseDocumentationList(documentationListData);
    const componentInfo = parseDocumentationComponent(componentData);

    // Handlers
    const handleComponentSelect = (componentKey) => {
        if (setSelectedComponent && typeof setSelectedComponent === "function") {
            setSelectedComponent(componentKey);
        }
        if (setSelectedFile && typeof setSelectedFile === "function") {
            setSelectedFile(null);
        }
    };

    const handleDownloadFile = async (component, filename, format = "pdf") => {
        // Use selectedComponent if component is undefined
        const actualComponent = component || selectedComponent;

        if (!actualComponent) {
            alert("No component selected. Please select a documentation component first.");
            return;
        }

        try {
            // Handle PDF download
            await handleDownloadPDF(actualComponent, filename);
        } catch (error) {
            console.error("Download failed:", error);
            alert(`Download failed: ${error.message || "Unknown error"}`);
        }
    };

    const handleDownloadPDF = async (component, filename) => {
        try {
            await downloadDocumentation({
                component,
                filename,
                format: "pdf",
            }).unwrap();
        } catch (error) {
            console.error("Error downloading PDF:", error);
            alert("Error downloading PDF. Please try again.");
        }
    };

    const handleViewFile = async (component, filename) => {
        const actualComponent = component || selectedComponent;
        if (!actualComponent) {
            alert("No component selected. Please select a documentation component first.");
            return;
        }

        try {
            const result = await viewDocumentation({
                component: actualComponent,
                filename,
            }).unwrap();

            if (!result?.blob) {
                throw new Error("Invalid PDF preview response");
            }

            const blobUrl = window.URL.createObjectURL(result.blob);
            const previewWindow = window.open(blobUrl, "_blank", "noopener,noreferrer");
            if (!previewWindow) {
                window.URL.revokeObjectURL(blobUrl);
                alert("Popup blocked. Please allow popups for this site to view PDF.");
                return;
            }

            setTimeout(() => {
                window.URL.revokeObjectURL(blobUrl);
            }, 60000);
        } catch (error) {
            console.error("Error viewing PDF:", error);
            alert("Error opening PDF preview. Please try again.");
        }
    };

    const handleRefresh = () => {
        refetchList();
        if (clearSelection && typeof clearSelection === "function") {
            clearSelection();
        }
    };

    const handleBackToComponents = () => {
        if (clearSelection && typeof clearSelection === "function") {
            clearSelection();
        }
    };

    // Render loading state
    if (isLoadingList) {
        return (
            <Box>
                <Grid container spacing={3}>
                    {[1, 2, 3, 4, 5].map((index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Card>
                                <CardContent>
                                    <Skeleton variant="text" width="80%" height={32} />
                                    <Skeleton variant="text" width="60%" height={24} />
                                    <Skeleton variant="rectangular" height={40} sx={{ mt: 2 }} />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }

    // Render error state
    if (listError) {
        return (
            <Box>
                <Alert severity="error" sx={{ mb: 2 }}>
                    Failed to load documentation: {listError?.data?.message || listError?.message}
                </Alert>
                <Button variant="contained" onClick={handleRefresh} startIcon={<RefreshIcon />}>
                    Retry
                </Button>
            </Box>
        );
    }

    return (
        <Box>
            {/* Header Actions */}
            <Box sx={{ mb: 3, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1 }}>
                <Button variant="outlined" onClick={handleRefresh} startIcon={<RefreshIcon />}>
                    Refresh
                </Button>
            </Box>
            {selectedComponent && componentInfo ? (
                <Box>
                    {/* Component Header */}
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                            <IconButton
                                onClick={handleBackToComponents}
                                sx={{ mr: 2 }}
                                title="Back to all documentation"
                            >
                                <BackIcon />
                            </IconButton>
                            <Typography variant="h5">{componentInfo.name}</Typography>
                        </Box>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                            {componentInfo.description}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                            {componentInfo.techStack.map((tech) => (
                                <Chip key={tech} label={tech} size="small" variant="outlined" />
                            ))}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            {componentInfo.totalFiles} files available
                        </Typography>
                    </Box>

                    {/* File List */}
                    <DocumentationCard
                        files={componentInfo.files}
                        onDownload={handleDownloadFile}
                        onView={handleViewFile}
                        isLoading={isLoadingComponent}
                        component={selectedComponent}
                    />
                </Box>
            ) : (
                /* Documentation Overview */
                <Box>
                    <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                        Available Documentation Components
                    </Typography>
                    <Grid container spacing={3}>
                        {documentationList.map((component) => (
                            <Grid item xs={12} sm={6} md={4} key={component.key}>
                                <Card
                                    sx={{
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                        "&:hover": {
                                            transform: "translateY(-4px)",
                                            boxShadow: 4,
                                        },
                                    }}
                                    onClick={() => handleComponentSelect(component.key)}
                                >
                                    <CardContent>
                                        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                            <Typography variant="h4" sx={{ mr: 2 }}>
                                                {component.icon}
                                            </Typography>
                                            <Typography variant="h6" component="div">
                                                {component.name}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {component.description}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Chip
                                                label={`${component.files.length} files`}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                            <Button
                                                size="small"
                                                startIcon={<ViewIcon />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleComponentSelect(component.key);
                                                }}
                                            >
                                                View
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}
        </Box>
    );
};

export default DocumentationManager;
